import React from 'react';

import styled from 'styled-components';
import { get } from 'lodash';
import { Show } from '../../show';

import GridContainer from '../../layouts/components/grid-container';
import FluidContainer from '../../layouts/components/fluid-container';

import { Question, PublicTitle, Attachments } from '../components';
import AudioPlayer from '../../audio-player';
import ImageGallery from '../../image-gallery';
import MathContent from '../../math-content';
import RadioGroup from '../../radio-group';
import SharedText from '../../shared-text';
import YouTubePlayer from '../../video-player/youtube';
import { exercisePropTypes, calculateHighlightTrueFalse, exerciseDefaultProps } from '../../../constants/exercise';
import Geogebra from '../../geogebra';
import Spacer from '../../spacer';
import Stack from '../../stack';

const CustomList = styled.ol`
    list-style-type: upper-alpha;
    padding-inline-start: initial;
    margin-left: calc(var(--space-unit) * 2);
    margin-bottom: 0;

    img {
        margin-left: calc(var(--space-unit) / 2);
        margin-right: calc(var(--space-unit) / 2);
        vertical-align: middle;
    }

    & > li {
        list-style-position: outside;
        font-weight: bold;
        margin-top: calc(var(--space-unit) * 2);
        margin-left: calc(var(--space-unit) / 2);
        & > * {
            font-weight: initial;
        }
    }
    & > li:first-child {
        margin-top: 0;
    }
    & > li:only-of-type {
        list-style-type: none;
    }
`;

const decorateOption = (correctionStatus, answers, question, option) => {
    const answer = answers.find((answer) => answer.questionId === `${question.id}`);
    const currentAnswer = !!answer && answer.value === option.id;
    return {
        ...option,
        disabled: !!correctionStatus,
        checked: currentAnswer,
        highlight: calculateHighlightTrueFalse(correctionStatus, option.correct, !!answer, currentAnswer),
    };
};

const TrueFalse = ({
    exercise,
    answers = [],
    onAnswer,
    correctionStatus,
    onPlayVideo,
    videos,
    show,
    hasAttachments = exerciseDefaultProps.hasAttachments,
}) => {
    const showGrid = (hasAttachments.images || hasAttachments.video) && show.attachments;

    const handleAnswer = (questionId, value) => {
        const isChecked = answers.find((answer) => answer.questionId === questionId && answer.value === value);
        const previousQuestions = answers.filter((answer) => answer.questionId !== questionId);

        if (isChecked) {
            onAnswer(exercise.id, [...previousQuestions]);
        } else {
            onAnswer(exercise.id, [...previousQuestions, { questionId, value }]);
        }
    };

    return (
        <>
            <Show when={show.title && exercise.publicTitle}>
                <FluidContainer paddingBottom={24}>
                    <PublicTitle>{exercise.publicTitle}</PublicTitle>
                </FluidContainer>
            </Show>

            <Show when={show.prompt && exercise.prompt}>
                <FluidContainer paddingBottom={24}>
                    <MathContent>{exercise.prompt}</MathContent>
                </FluidContainer>
            </Show>

            <Show when={show.attachments && hasAttachments.geogebra}>
                <FluidContainer paddingBottom={24}>
                    <Geogebra exerciseId={exercise.id} src={exercise.attachments.geogebra?.src} />
                </FluidContainer>
            </Show>

            <Show when={show.sharedText && hasAttachments.sharedText}>
                <FluidContainer paddingBottom={24} flexWrap="wrap">
                    <SharedText exercise={exercise} />
                </FluidContainer>
            </Show>

            {show.attachments && hasAttachments.audio && (
                <FluidContainer paddingBottom={24}>
                    <AudioPlayer source={exercise.attachments.audio?.src} />
                </FluidContainer>
            )}

            <GridContainer columns={[1, showGrid ? 2 : 1]} paddingBottom={24}>
                <Attachments show={showGrid}>
                    <Stack gap="desktop">
                        {hasAttachments.video && (
                            <YouTubePlayer
                                videoIdOrSource={exercise.attachments.video?.src}
                                onPlayVideo={() => onPlayVideo(exercise.id)}
                                showPreview={!videos.find((item) => item.exerciseId === exercise.id)}
                            />
                        )}
                        {hasAttachments.images && <ImageGallery images={get(exercise, 'attachments.images')} />}
                    </Stack>
                </Attachments>
                <div>
                    {exercise.question && <Question>{exercise.question}</Question>}
                    <CustomList>
                        {exercise.questions.map((question) => {
                            const options = question.options.map((option) => decorateOption(correctionStatus, answers, question, option));
                            return (
                                <li key={question.id}>
                                    <MathContent>{question.question}</MathContent>
                                    <Spacer size="small" />
                                    <RadioGroup
                                        exerciseId={exercise.id}
                                        options={options}
                                        name={`assertion-${question.id}`}
                                        onClick={(value) => handleAnswer(`${question.id}`, value)}
                                    />
                                </li>
                            );
                        })}
                    </CustomList>
                </div>
            </GridContainer>
        </>
    );
};

TrueFalse.propTypes = exercisePropTypes;

export default TrueFalse;
